import type { TurnstileEvent } from '@kompare/captcha-react';
import apm from 'src/apm';

/** Where in the funnel the widget lives; a label on every event. */
export type CaptchaPage = 'property-funnel-customer' | 'property-webshop-risks';

/**
 * What the visitor submitted, reduced to what is safe to send to APM. The phone number
 * (`input.data.phone` on createSo) itself is never labelled (PII); its digit count
 * answers the question the server cannot (request bodies are not logged anywhere): was
 * the visitor challenged on a real number or an empty/garbage one? Label names match
 * ao-checkout's and dzo-webshop's (`input_kind`, `input_length`) so cross-funnel
 * dashboards line up.
 */
export type CaptchaInput = {
  kind: 'phone' | 'empty';
  length: number;
};

export function describeInput(value: string | null | undefined): CaptchaInput {
  const digits = (value ?? '').replace(/\D/g, '');
  return { kind: digits.length ? 'phone' : 'empty', length: digits.length };
}

// The funnel we want to see per challenge and user agent in APM:
//   submit 428 → captcha-required → rendered → token → submit <status> (200 | 428 again
//   | 429 | 403 | 500) — or nothing, which is an abandon.
// Every event is its own short custom transaction (`turnstile <event>`, type `captcha`)
// carrying the labels below, so "rendered but never produced a token" is a plain query
// on traces-apm.rum-*. Failures are additionally APM errors (logs-apm.error-*, grouped
// by message). Everything is a no-op when apm is undefined (local development) and
// never throws — telemetry must not break the form.
//
// Stitching: the agent stamps `session.id` only on the page-load transaction
// (TransactionService.setSession in apm-rum-core 5.25), never on custom ones, so the
// events above would be unrelated documents. The hook therefore mints a `challenge_id`
// when the server first answers CAPTCHA_REQUIRED and passes it to every event of that
// challenge, the denied submit included, up to the submit that ends it. Group by
// `labels.challenge_id` in Kibana; a challenge with no `token` or no final `submit`
// is an abandon. Random, per challenge, no link to any person or cookie.
//
// Deliberately NOT apm.startSpan()/apm.addLabels(): with no active transaction (the
// widget usually renders while the page is idle) startSpan() opens a *temporary*
// transaction the agent drops at end, and addLabels() writes global agent context, so
// every later transaction and error would inherit a stale page/turnstile_event.
// An explicit unmanaged transaction is reported on its own and keeps the labels scoped.

const FAILURES = new Set<TurnstileEvent['type']>(['script-failed', 'error', 'unsupported', 'timeout']);

type Labels = Record<string, string | number | boolean>;

function labelsFor(page: CaptchaPage, event: string, code?: string, extra: Labels = {}): Labels {
  return {
    page,
    turnstile_event: event,
    turnstile_code: code ?? '',
    ...extra,
  };
}

function inputLabels(input?: CaptchaInput): Labels {
  if (!input) return {};
  return { input_kind: input.kind, input_length: input.length };
}

function challengeLabel(challengeId?: string): Labels {
  return challengeId ? { challenge_id: challengeId } : {};
}

/** A fresh correlation id for one challenge (`crypto.randomUUID` where available). */
export function newChallengeId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function record(page: CaptchaPage, event: string, code?: string, extra: Labels = {}) {
  if (!apm) return;
  try {
    const labels = labelsFor(page, event, code, extra);
    const tr = apm.startTransaction(`turnstile ${event}`, 'captcha', { managed: false });
    tr?.addLabels(labels);
    // The agent silently discards transactions whose duration is 0
    // (PerformanceMonitoring.filterTransaction), and start→end in the same tick rounds to 0.
    // End it explicitly 1 ms after now; the duration itself carries no meaning.
    tr?.end(performance.now() + 1);
    if (FAILURES.has(event as TurnstileEvent['type'])) {
      const err = new Error(`turnstile ${event}${code ? ` ${code}` : ''}`);
      err.name = 'TurnstileError';
      apm.captureError(err, { labels });
    }
  } catch {
    // never let telemetry surface to the user
  }
}

/**
 * The server answered CAPTCHA_REQUIRED: the widget is about to be armed. `input` is
 * the submitted phone number, reduced to kind/length here; `challengeId` is the id the
 * hook minted for this challenge.
 */
export function reportCaptchaRequired(page: CaptchaPage, challengeId: string, input?: CaptchaInput) {
  record(page, 'captcha-required', undefined, { ...challengeLabel(challengeId), ...inputLabels(input) });
}

/** Wire to `<TurnstileWidget onEvent>`; `challengeId` is the challenge the widget is solving. */
export function reportTurnstileEvent(page: CaptchaPage, event: TurnstileEvent, challengeId?: string) {
  record(page, event.type, event.type === 'error' ? event.code : undefined, challengeLabel(challengeId));
}

/**
 * Outcome of every createSo creation submit — `status` from `submitStatusOf()` (200 on
 * success). With `with_token` this tells "solved, then rejected again" (siteverify
 * down, wrong secret) apart from a plain captcha abandon, which server-side counting
 * cannot. `challengeId` is set on the submit that opened a challenge and on every
 * retry within it; an unchallenged submit has none.
 */
export function reportSubmitOutcome(
  page: CaptchaPage,
  status: number,
  input: CaptchaInput,
  withToken: boolean,
  challengeId?: string,
) {
  record(page, 'submit', undefined, {
    ...challengeLabel(challengeId),
    ...inputLabels(input),
    submit_status: status,
    with_token: withToken,
  });
}
